// Columna derecha del explorer (`.col-detail` del mock): el hero del crédito, la fila de acciones y
// las 5 pestañas —Agenda · Contactos · Compromisos · Pagos · Comunicación— con sus notas al pie.
import { useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/kit';
import { FileDrawerTabs, type FileDrawerTab } from '@/shared/patterns/FileDrawer';
import type { CreditTone } from '@/domains/credit/creditFile/logic';
import {
    COMMUNICATION_TEMPLATES,
    CONTACT_CHANNEL_LABEL,
    CONTACT_CHANNEL_SHORT,
    CONTACT_OUTCOME_LABEL,
    CONTACT_OUTCOME_SHORT,
    PAYMENT_CHANNEL_LABEL,
    PROMISE_STATE_LABEL,
    PROMISE_STATE_TONE,
    contactAuthorLabel,
    creditStatusChip,
    formatAgendaMoney,
    formatShortDay,
} from '../format';
import {
    arrearsBucketLabel,
    contextAlert,
    daysBetween,
    lastContactOf,
    unlockRule,
    type AgendaBanner,
} from '../logic';
import { COLLECTOR_NAMES } from '../fixtures';
import type {
    AgendaContact,
    AgendaDrawerKey,
    AgendaPayment,
    AgendaPromise,
    CollectorAgendaRow,
    ContactChannel,
} from '../types';

const AGENDA_TABS: readonly FileDrawerTab[] = [
    { key: 'agenda', label: 'Agenda' },
    { key: 'contacts', label: 'Contactos' },
    { key: 'promises', label: 'Compromisos' },
    { key: 'payments', label: 'Pagos' },
    { key: 'comms', label: 'Comunicación' },
];

const BANNER_TONE: Record<AgendaBanner['tone'], string> = {
    info: 'bg-info-soft text-info',
    warning: 'bg-warning-soft text-warning',
    danger: 'bg-danger-soft text-danger',
    success: 'bg-success-soft text-success',
};

const CHIP_TONE: Record<CreditTone, string> = {
    success: 'border-success-soft-border bg-success-soft text-success',
    danger: 'border-danger-soft-border bg-danger-soft text-danger',
    warning: 'border-warning-soft-border bg-warning-soft text-warning',
    info: 'border-info-soft-border bg-info-soft text-info',
    neutral: 'border-border bg-muted text-muted-foreground',
};

const DOT_TONE: Record<CreditTone, string> = {
    success: 'bg-success',
    danger: 'bg-danger',
    warning: 'bg-warning',
    info: 'bg-info',
    neutral: 'bg-muted-foreground',
};

/** El icono de canal del historial, como el mock. */
const CHANNEL_ICON: Record<ContactChannel, string> = {
    call: '📞',
    sms: '💬',
    chat: '💬',
    whatsapp: '💬',
    voice_blaster: '🔊',
};

const SECTION_TITLE =
    'mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground';
const FOOTNOTE = 'mt-3 text-[10px] text-muted-foreground';

function Chip({ tone, children }: { tone: CreditTone; children: ReactNode }) {
    return (
        <span
            className={cn(
                'inline-flex items-center whitespace-nowrap rounded-full border px-[5px] py-px text-[9px] font-medium',
                CHIP_TONE[tone],
            )}
        >
            {children}
        </span>
    );
}

/** Una de las 4 tarjetas de «Estado a hoy». */
function StateCard({
    label,
    value,
    sub,
    valueClassName,
}: {
    label: string;
    value: string;
    sub: string;
    valueClassName?: string;
}) {
    return (
        <div className="rounded-md border border-border p-2">
            <div className="text-[9.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                {label}
            </div>
            <div className={cn('text-[16px] font-semibold tabular-nums', valueClassName)}>
                {value}
            </div>
            <div className="text-[10px] text-muted-foreground">{sub}</div>
        </div>
    );
}

function InfoRow({ label, value }: { label: string; value: string }) {
    return (
        <div className="grid grid-cols-[78px_1fr] gap-1.5 py-[3px] text-[11.5px]">
            <span className="text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
                {label}
            </span>
            <span className="tabular-nums">{value}</span>
        </div>
    );
}

function Banner({ banner }: { banner: AgendaBanner }) {
    return (
        <div className={cn('rounded-md p-2 text-[11px] leading-relaxed', BANNER_TONE[banner.tone])}>
            {banner.text}
        </div>
    );
}

/** Una entrada de cualquiera de los tres historiales (`.tl-item` del mock). */
function TimelineItem({ tone, children }: { tone: CreditTone; children: ReactNode }) {
    return (
        <div className="flex gap-2 border-b border-border/50 py-1.5 text-[11.5px] last:border-b-0">
            <div className={cn('mt-[5px] size-1.5 flex-none rounded-full', DOT_TONE[tone])} />
            <div className="min-w-0 flex-1">{children}</div>
        </div>
    );
}

function ContactItem({ contact }: { contact: AgendaContact }) {
    return (
        <TimelineItem tone="info">
            <div className="flex items-center justify-between gap-1">
                <div className="text-[11.5px]">
                    <span>
                        {CHANNEL_ICON[contact.channel]} {CONTACT_CHANNEL_SHORT[contact.channel]}
                    </span>{' '}
                    <span className="text-muted-foreground">
                        · {CONTACT_OUTCOME_SHORT[contact.outcome]}
                    </span>
                </div>
                <div className="text-[10px] tabular-nums text-muted-foreground">
                    {contact.at.slice(0, 10)} {contact.at.slice(11, 16)}
                </div>
            </div>
            {contact.note === '' ? null : (
                <div className="mt-0.5 text-[10.5px] text-muted-foreground">{contact.note}</div>
            )}
            <div className="mt-0.5 text-[9.5px] text-muted-foreground">
                {contactAuthorLabel(contact.collectorId, COLLECTOR_NAMES[contact.collectorId])}
            </div>
        </TimelineItem>
    );
}

function PromiseItem({ promise }: { promise: AgendaPromise }) {
    const tone = PROMISE_STATE_TONE[promise.state];
    return (
        <TimelineItem tone={tone}>
            <div className="flex items-center justify-between gap-1">
                <div className="text-[11.5px] font-medium">
                    {formatShortDay(promise.day)} · {formatAgendaMoney(promise.amountCents)}
                </div>
                <Chip tone={tone}>{PROMISE_STATE_LABEL[promise.state]}</Chip>
            </div>
            <div className="text-[10px] text-muted-foreground">
                Vía {PAYMENT_CHANNEL_LABEL[promise.channel]} · registrado{' '}
                {formatShortDay(promise.registeredOn)}
            </div>
        </TimelineItem>
    );
}

function PaymentItem({ payment }: { payment: AgendaPayment }) {
    return (
        <TimelineItem tone="success">
            <div className="flex items-center justify-between gap-1">
                <div>Cupón #{payment.installmentNumber}</div>
                <div className="font-semibold tabular-nums text-success">
                    {formatAgendaMoney(payment.amountCents)}
                </div>
            </div>
            <div className="text-[10px] text-muted-foreground">
                {formatShortDay(payment.paidOn)} · {PAYMENT_CHANNEL_LABEL[payment.channel]}
            </div>
        </TimelineItem>
    );
}

export interface AgendaDetailProps {
    row: CollectorAgendaRow | null;
    today: string;
    onOpenDrawer: (drawer: AgendaDrawerKey) => void;
    className?: string;
}

export function AgendaDetail({ row, today, onOpenDrawer, className }: AgendaDetailProps) {
    const [activeTab, setActiveTab] = useState('agenda');

    if (row === null) {
        return (
            <div
                role="status"
                className={cn(
                    'flex flex-1 items-center justify-center px-6 py-10 text-center text-[12px] text-muted-foreground',
                    className,
                )}
            >
                Elige un crédito de la agenda para ver su ficha.
            </div>
        );
    }

    const status = creditStatusChip(row.status, row.overdueDays);
    const last = lastContactOf(row);
    const daysSince = last === null ? null : daysBetween(last.at, today);
    const deviceLocked = row.device?.state === 'locked';
    const deviceLabel =
        row.device === null ? 'Sin dispositivo' : deviceLocked ? 'Bloqueado' : 'Activo';
    const deviceSub =
        row.device === null
            ? '—'
            : row.device.lockedSince === undefined
              ? `IMEI ${row.device.imei.slice(-6)}`
              : `desde ${formatShortDay(row.device.lockedSince)}`;

    return (
        <div className={cn('flex min-h-0 flex-1 flex-col', className)}>
            <div className="flex-none border-b border-border px-3.5 py-3">
                <div className="mb-1 flex items-start justify-between gap-2">
                    <div className="text-[9.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        Crédito
                    </div>
                    <Chip tone="neutral">{formatShortDay(row.originatedOn)}</Chip>
                </div>
                <div className="font-mono text-[15px] font-semibold leading-tight">
                    {/* eslint-disable-next-line no-restricted-syntax -- el folio del crédito ES el título de la ficha en el mock */}
                    {row.creditId}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <Chip tone={status.tone}>{status.label}</Chip>
                    <Chip tone="neutral">{row.productLabel}</Chip>
                </div>
                <div className="mt-2 text-[11.5px]">
                    {/* El mock enlaza a la ficha 360; el Portal Cobrador todavía no tiene la suya. */}
                    <span className="font-medium">{row.customerName}</span>
                    <span className="mx-1 text-muted-foreground">·</span>
                    <span className="text-[10.5px] text-muted-foreground">{row.storeName}</span>
                </div>
            </div>

            <div className="flex flex-none flex-wrap gap-1.5 px-3.5 pt-2">
                <Button
                    size="sm"
                    onClick={() => {
                        onOpenDrawer('contact');
                    }}
                >
                    Registrar contacto
                </Button>
                <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                        onOpenDrawer('promise');
                    }}
                >
                    Compromiso
                </Button>
                <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                        onOpenDrawer('payment');
                    }}
                >
                    Pago
                </Button>
                <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                        onOpenDrawer('link');
                    }}
                >
                    Enviar link
                </Button>
                <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                        onOpenDrawer('more');
                    }}
                >
                    Más
                </Button>
            </div>

            <FileDrawerTabs
                className="mt-1.5"
                tabs={AGENDA_TABS}
                activeTab={activeTab}
                onChange={setActiveTab}
            />

            <div className="min-h-0 flex-1 overflow-y-auto px-3.5 py-3">
                {activeTab === 'agenda' ? (
                    <>
                        <div className={SECTION_TITLE}>Estado a hoy</div>
                        <div className="mb-3 grid grid-cols-2 gap-2">
                            <StateCard
                                label="Cuotas vencidas"
                                value={String(row.overdueInstallments)}
                                sub={`${formatAgendaMoney(row.overdueAmountCents)} vencido`}
                                valueClassName="text-danger"
                            />
                            <StateCard
                                label="DPI mora"
                                value={row.overdueDays > 0 ? `${String(row.overdueDays)} d` : '—'}
                                sub={arrearsBucketLabel(row.overdueDays)}
                                valueClassName="text-danger"
                            />
                            <StateCard
                                label="Atrasos acumulados"
                                value={String(row.accumulatedArrears)}
                                sub={row.accumulatedArrears >= 3 ? 'Reincidente' : 'Normal'}
                            />
                            <StateCard
                                label="Dispositivo"
                                value={deviceLabel}
                                sub={deviceSub}
                                valueClassName={cn(
                                    'text-[13px]',
                                    deviceLocked ? 'text-danger' : 'text-success',
                                )}
                            />
                        </div>

                        <div className={SECTION_TITLE}>Regla de desbloqueo (M2 §7.3, M4 §2.3)</div>
                        <div className="mb-3">
                            <Banner banner={unlockRule(row, formatAgendaMoney)} />
                        </div>

                        <div className={SECTION_TITLE}>Último contacto</div>
                        {last === null ? (
                            <>
                                <InfoRow label="Cuándo" value="Sin contacto registrado" />
                                <InfoRow label="Canal" value="Sin contacto registrado" />
                                <InfoRow label="Resultado" value="Sin contacto registrado" />
                                <InfoRow label="Cobrador" value="Sin contacto registrado" />
                            </>
                        ) : (
                            <>
                                <InfoRow
                                    label="Cuándo"
                                    value={`${last.at.slice(0, 10)} · ${
                                        daysSince === 0
                                            ? 'hoy'
                                            : daysSince === 1
                                              ? 'ayer'
                                              : `hace ${String(daysSince)} días`
                                    }`}
                                />
                                <InfoRow
                                    label="Canal"
                                    value={CONTACT_CHANNEL_LABEL[last.channel]}
                                />
                                <InfoRow
                                    label="Resultado"
                                    value={CONTACT_OUTCOME_LABEL[last.outcome]}
                                />
                                <InfoRow
                                    label="Cobrador"
                                    value={contactAuthorLabel(
                                        last.collectorId,
                                        COLLECTOR_NAMES[last.collectorId],
                                    )}
                                />
                            </>
                        )}

                        <div className="mt-3">
                            <Banner
                                banner={contextAlert(
                                    row,
                                    today,
                                    formatAgendaMoney,
                                    formatShortDay,
                                    (channel) => PAYMENT_CHANNEL_LABEL[channel],
                                )}
                            />
                        </div>
                    </>
                ) : null}

                {activeTab === 'contacts' ? (
                    <>
                        <div className="mb-2 flex items-center justify-between">
                            <div className="text-[11.5px] font-semibold">Historial de contacto</div>
                            <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => {
                                    onOpenDrawer('contact');
                                }}
                            >
                                + Registrar
                            </Button>
                        </div>
                        {row.contacts.length === 0 ? (
                            <div className="py-4 text-center text-[11.5px] text-muted-foreground">
                                Sin contactos registrados.
                            </div>
                        ) : (
                            [...row.contacts]
                                .sort((left, right) => right.at.localeCompare(left.at))
                                .map((contact) => (
                                    <ContactItem key={contact.at} contact={contact} />
                                ))
                        )}
                        <div className={FOOTNOTE}>
                            Cobrador no llama desde su número personal — todo pasa por la plataforma
                            (M4 §6.4).
                        </div>
                    </>
                ) : null}

                {activeTab === 'promises' ? (
                    <>
                        <div className="mb-2 flex items-center justify-between">
                            <div className="text-[11.5px] font-semibold">Compromisos de pago</div>
                            <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => {
                                    onOpenDrawer('promise');
                                }}
                            >
                                + Registrar
                            </Button>
                        </div>
                        {row.promises.length === 0 ? (
                            <div className="py-4 text-center text-[11.5px] text-muted-foreground">
                                Sin compromisos registrados.
                            </div>
                        ) : (
                            row.promises.map((promise) => (
                                <PromiseItem
                                    key={`${promise.day}-${promise.registeredOn}`}
                                    promise={promise}
                                />
                            ))
                        )}
                        <div className={FOOTNOTE}>
                            Compromiso incumplido → cobrador tiene 24h para nuevo contacto o alerta
                            al supervisor (M4 §4.5).
                        </div>
                    </>
                ) : null}

                {activeTab === 'payments' ? (
                    <>
                        <div className="mb-2 flex items-center justify-between">
                            <div className="text-[11.5px] font-semibold">Pagos aplicados</div>
                            <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => {
                                    onOpenDrawer('payment');
                                }}
                            >
                                + Efectivo
                            </Button>
                        </div>
                        {row.payments.length === 0 ? (
                            <div className="py-4 text-center text-[11.5px] text-muted-foreground">
                                Sin pagos aplicados.
                            </div>
                        ) : (
                            row.payments.map((payment) => (
                                <PaymentItem key={payment.installmentNumber} payment={payment} />
                            ))
                        )}
                        <div className={FOOTNOTE}>
                            Pago en tienda → folio único genera desbloqueo inmediato (M4 §3.2).
                        </div>
                    </>
                ) : null}

                {activeTab === 'comms' ? (
                    <>
                        <div className="mb-2 flex items-center justify-between">
                            <div className="text-[11.5px] font-semibold">
                                Plantillas por día de mora (M4 §6.3)
                            </div>
                        </div>
                        {COMMUNICATION_TEMPLATES.map((template) => {
                            const day = Number(template.day);
                            const sent = row.overdueDays >= day;
                            const current = row.overdueDays === day;
                            return (
                                <div
                                    key={template.day}
                                    className={cn(
                                        'flex gap-2 border-b border-border/50 py-1.5 last:border-b-0',
                                        sent ? '' : 'opacity-50',
                                    )}
                                >
                                    <div
                                        className={cn(
                                            'mt-[5px] size-1.5 flex-none rounded-full',
                                            DOT_TONE[template.tone],
                                        )}
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center justify-between gap-1">
                                            <div className="text-[11px]">
                                                <span className="font-semibold">
                                                    Día {template.day}
                                                </span>{' '}
                                                <span className="text-muted-foreground">
                                                    · {template.channel}
                                                </span>
                                            </div>
                                            <Chip
                                                tone={
                                                    sent
                                                        ? current
                                                            ? 'info'
                                                            : 'success'
                                                        : 'neutral'
                                                }
                                            >
                                                {current ? 'Hoy' : sent ? 'Enviada' : 'Pendiente'}
                                            </Chip>
                                        </div>
                                        <div className="mt-0.5 text-[10.5px] text-muted-foreground">
                                            {template.text}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        <div className={FOOTNOTE}>
                            Horario permitido 8:00-21:00 hrs (Ley Protección al Deudor).
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
}

/** Se exporta para el candado: si alguien borra una pestaña del mock, el test muere. */
export { AGENDA_TABS };
